import type { PlanPublico, PlataformaCompetidora } from '@medchoice/shared';
import { Icono, Insignia, type TonoInsignia } from '../../ui';
import { pesos } from '../../ui/format';

const GRATIS: Record<PlataformaCompetidora['plan_gratuito'], { tono: TonoInsignia; texto: string }> = {
  si: { tono: 'confirmado', texto: 'Plan gratis completo' },
  limitado: { tono: 'pendiente', texto: 'Gratis limitado' },
  no: { tono: 'neutral', texto: 'Sin plan gratis' },
};

/** Cada marca de la escala vale diez mil pesos. */
const PASO_DE_ESCALA = 1_000_000;

function Barra({ minimo, maximo, escala }: { minimo: number; maximo: number | null; escala: number }) {
  const abierta = maximo === null;
  const ancho = abierta ? escala - minimo : Math.max(maximo - minimo, 0);
  const posicion = (monto: number) => `${Math.min(monto / escala, 1) * 100}%`;
  return (
    <span className={`comparacion__barra${abierta ? ' comparacion__barra--abierta' : ''}${ancho === 0 ? ' comparacion__barra--punto' : ''}`}
      style={{ left: posicion(minimo), width: ancho === 0 ? undefined : posicion(ancho) }} />
  );
}

/** Dónde cae MedChoice frente a las plataformas que ya usan los profesionales, en una misma escala de precio. */
export function Comparacion({ planes, competencia }: { planes: PlanPublico[]; competencia: PlataformaCompetidora[] }) {
  const maximo = Math.max(...planes.map((p) => p.precio_mensual));
  const tope = Math.max(maximo, ...competencia.map((c) => c.precio_maximo ?? c.precio_minimo));
  // La escala se redondea para arriba a la marca siguiente y deja aire a la derecha.
  const escala = Math.ceil((tope * 1.05) / PASO_DE_ESCALA) * PASO_DE_ESCALA;
  const marcas = Array.from({ length: escala / PASO_DE_ESCALA + 1 }, (_, i) => i * PASO_DE_ESCALA);
  const posicion = (monto: number) => `${Math.min(monto / escala, 1) * 100}%`;
  const primerPlanPago = planes.filter((p) => p.precio_mensual > 0).sort((a, b) => a.precio_mensual - b.precio_mensual)[0];
  const entradaDeConsultorios = Math.min(...competencia.filter((c) => c.plan_gratuito === 'no').map((c) => c.precio_minimo));

  return (
    <section className="comparacion">
      <header className="comparacion__encabezado">
        <h2>Cómo nos comparamos</h2>
        <p>
          Precio mensual de cada plataforma, en la misma escala.
          {competencia.every((c) => c.plan_gratuito !== 'si') && ' MedChoice es la única con un plan gratis completo.'}
        </p>
      </header>

      <div className="comparacion__tabla" role="table" aria-label="Precio mensual por plataforma">
        <div className="comparacion__fila comparacion__fila--escala" role="row" aria-hidden="true">
          <span />
          <span className="comparacion__pista">
            {marcas.map((m) => (
              <span key={m} className="comparacion__marca" style={{ left: posicion(m) }}>{m === 0 ? '$0' : `${m / 100_000} mil`}</span>
            ))}
          </span>
          <span />
        </div>

        {competencia.map((c, i) => (
          <div key={c.nombre} className="comparacion__fila" role="row" style={{ animationDelay: `${i * 60}ms` }}>
            <span className="comparacion__nombre" role="rowheader">
              <strong>{c.nombre}</strong>
              <span>{c.publico}</span>
            </span>
            <span className="comparacion__pista" role="cell">
              <Barra minimo={c.precio_minimo} maximo={c.precio_maximo} escala={escala} />
              <span className="comparacion__monto" style={{ left: posicion(c.precio_minimo) }}>
                {c.precio_maximo === null ? `desde ${pesos(c.precio_minimo)}`
                  : c.precio_maximo === c.precio_minimo ? pesos(c.precio_minimo)
                    : `${pesos(c.precio_minimo)} a ${pesos(c.precio_maximo)}`}
                {c.nota && ` ${c.nota}`}
              </span>
            </span>
            <span role="cell">
              <Insignia tono={GRATIS[c.plan_gratuito].tono} tamano="chico">{GRATIS[c.plan_gratuito].texto}</Insignia>
            </span>
          </div>
        ))}

        <div className="comparacion__fila comparacion__fila--nosotros" role="row" style={{ animationDelay: `${competencia.length * 60}ms` }}>
          <span className="comparacion__nombre" role="rowheader">
            <strong>MedChoice</strong>
            <span>Profesionales independientes</span>
          </span>
          <span className="comparacion__pista" role="cell">
            <Barra minimo={0} maximo={maximo} escala={escala} />
            {planes.map((p) => (
              <span key={p.id_plan} className="comparacion__plan" style={{ left: posicion(p.precio_mensual) }}>
                <span className="comparacion__punto" />
                <span className="comparacion__etiqueta">
                  {p.nombre}<br /><strong>{p.precio_mensual === 0 ? 'Gratis' : pesos(p.precio_mensual)}</strong>
                </span>
              </span>
            ))}
          </span>
          <span role="cell">
            <Insignia tono={GRATIS.si.tono} tamano="chico" icono="check">{GRATIS.si.texto}</Insignia>
          </span>
        </div>
      </div>

      <ul className="comparacion__claves">
        <li><Icono nombre="check-circle" tamano={18} /> Arrancás gratis y con todo lo esencial: perfil verificado, agenda y cobro de seña.</li>
        <li><Icono nombre="check-circle" tamano={18} /> Pensada para profesionales independientes, no para clínicas.</li>
        {primerPlanPago && primerPlanPago.precio_mensual < entradaDeConsultorios && (
          <li>
            <Icono nombre="check-circle" tamano={18} /> El plan {primerPlanPago.nombre} cuesta {pesos(primerPlanPago.precio_mensual)}: menos que
            la entrada de las plataformas sin plan gratis, desde {pesos(entradaDeConsultorios)}.
          </li>
        )}
      </ul>
      <p className="comparacion__fuente">Precios de lista relevados para el plan de negocios. Pueden variar.</p>
    </section>
  );
}
